import React, { useEffect, useRef, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { usePortal } from '../contexts/PortalContext';
import { brand } from '../data/projects';
import { Projects } from './portal/Projects';
import { ProjectAdmin } from './portal/ProjectAdmin';
import { Team } from './portal/Team';
import { StaffAccount } from '../components/portal/StaffAccount';

/** First name only, from the signed-in session — never a fixed name. */
function firstName(name: string | undefined): string {
  const first = (name ?? '').trim().split(/\s+/)[0];
  return first || 'Staff';
}
export function PortalApp() {
  const {
    portalRoute,
    goPortal,
    staffUser,
    staffSignOut
  } = usePortal();
  const [accountOpen, setAccountOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Closes on a click elsewhere, or on Escape.
  useEffect(() => {
    if (!menuOpen) return;
    function onPointerDown(event: MouseEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);
  return <div className="min-h-screen w-full bg-ink text-paper" data-id="element-896" data-mp="1q8rtx1kng9v">
      <header className="border-b border-ruleDark" data-id="element-897" data-mp="1rtlzljz4x77">
        <div className="mx-auto flex max-w-portal items-center justify-between gap-8 px-6 py-6 sm:px-10" data-id="element-898" data-mp="glf0krs1xt6w">
          {/* The supplied wordmark, in white — proportions untouched. */}
          <div data-id="element-899" data-mp="2idbbq0ab5115">
            <img src={brand.lockup} alt="Portal Productions" className="h-5 w-auto brightness-0 invert sm:h-6" data-id="element-900" data-mp="2iew5vospmhyh" />
            <p className="label-sm m-0 mt-2.5 text-paper/35" data-id="element-901" data-mp="333faijihsu6m">Studio Admin</p>
          </div>

          {/* Two matched outlined controls, then the account name set apart. */}
          <div className="flex items-center gap-3.5" data-id="element-902" data-mp="376k8c8rmo7ox">
            {/* Owners only. The Edge Function re-checks the role server-side. */}
            {staffUser?.role === 'owner' && <button type="button" onClick={() => goPortal('team')} className="label inline-flex h-10 items-center border border-paper/30 px-4 text-paper/70 transition-colors duration-150 ease-out hover:border-paper hover:text-paper" data-id="element-903" data-mp="3h0u0pqgkl5zn">
                Team
              </button>}

            <button type="button" onClick={() => {
            goPortal('projects');
            setCreating(true);
          }} className="label inline-flex h-10 items-center gap-3 border border-paper/30 px-6 text-paper/70 transition-colors duration-150 ease-out hover:border-paper hover:text-paper" data-id="element-904" data-mp="3mz2au0ju18p">
              <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-905" data-mp="1t55syvtya0up" />
              New Project
            </button>

            <div ref={menuRef} className="relative ml-4" data-id="element-906" data-mp="1o5jdefqng8iq">
              <button type="button" onClick={() => setMenuOpen(open => !open)} aria-haspopup="menu" aria-expanded={menuOpen} className="label inline-flex items-center gap-2.5 text-paper/50 transition-colors duration-150 ease-out hover:text-paper" data-id="element-907" data-mp="1o85k6d5pujzw">
                {firstName(staffUser?.name)}
                <span aria-hidden="true" className="text-[0.6rem] leading-none" data-id="element-908" data-mp="27344zukstl71">
                  ▾
                </span>
              </button>

              {menuOpen && <div role="menu" className="absolute right-0 top-full z-30 mt-4 min-w-[10rem] border border-paper/20 bg-ink" data-id="element-909" data-mp="2zlzvkgtl939a">
                  <button type="button" role="menuitem" onClick={() => {
                setMenuOpen(false);
                setAccountOpen(true);
              }} className="label block w-full px-5 py-4 text-left text-paper/60 transition-colors duration-150 ease-out hover:text-paper" data-id="element-910" data-mp="2fc8ewszj75rs">
                    Account
                  </button>
                  <button type="button" role="menuitem" onClick={() => {
                setMenuOpen(false);
                staffSignOut();
              }} className="label block w-full border-t border-paper/15 px-5 py-4 text-left text-paper/60 transition-colors duration-150 ease-out hover:text-paper" data-id="element-911" data-mp="2y5c33ytzcqf0">
                    Sign Out
                  </button>
                </div>}
            </div>
          </div>
        </div>
      </header>

      {accountOpen && <StaffAccount onClose={() => setAccountOpen(false)} data-id="element-912" data-mp="1abiag1vyaj68" />}

      <main className="mx-auto max-w-portal px-6 pb-32 pt-12 sm:px-10 sm:pt-14" data-id="element-913" data-mp="15dgqzfum4hu8">
        {portalRoute === 'projects' && <Projects creating={creating} setCreating={setCreating} data-id="element-914" data-mp="1k2v5f2d1ppgh" />}
        {portalRoute === 'project' && <ProjectAdmin data-id="element-915" data-mp="1f4tmchfmdq50" />}
        {portalRoute === 'team' && <Team data-id="element-916" data-mp="reh52fyhih1r" />}
      </main>
    </div>;
}